'use client';

import { useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import EventPaymentForm from '@/components/event-payment/event-payment-form';
import { resetSelectedTickets, toggleForm } from '@/store/event-payment/event-payment.actions';
import { getIsFormOpen } from '@/store/event-payment/event-payment.selectotrs';
import { EventDetails } from '@/types/events';
import { WithLang } from '@/types/general';
import { TicketsBookInput, TicketSelectOption } from '@/types/tickets';

interface Props {
    event: EventDetails;
    ticketOptions: TicketSelectOption[];
}

export default function EventPayment(props: WithLang<Props>) {
    const dispatch = useDispatch();
    const isFormOpen = useSelector(getIsFormOpen);

    const ticketsRef = useRef<HTMLDivElement>(null);
    const methods = useForm<TicketsBookInput>();

    const withTickets =
        props.ticketOptions?.length > 0 &&
        +props.event.visit_price > 0 &&
        props.event.status === 'scheduled' &&
        props.event.payment_currency.length > 0;

    useEffect(() => {
        if (isFormOpen) {
            setTimeout(() => {
                if (ticketsRef.current) {
                    const offset = 160;
                    const elementTop = ticketsRef.current.getBoundingClientRect().top + window.scrollY;
                    window.scrollTo({
                        top: elementTop - offset,
                        behavior: 'smooth',
                    });
                }
            }, 0);
        }
    }, [isFormOpen]);

    const closePaymentForm = () => {
        dispatch(toggleForm());
        window.scroll({ top: 0, behavior: 'smooth' });
    };

    useEffect(() => {
        return () => {
            dispatch(resetSelectedTickets());
        };
    }, [dispatch]);

    return (
        withTickets && (
            <FormProvider {...methods}>
                <div className="h-min" ref={ticketsRef}>
                    <EventPaymentForm
                        onClose={closePaymentForm}
                        event={props.event}
                        ticketOptions={props.ticketOptions}
                        lang={props.lang}
                        isVisible={isFormOpen}
                    />
                </div>
            </FormProvider>
        )
    );
}
